import type { ReactNode } from 'react';
import { cn } from './lib/cn';

export type EmptyStateProps = {
  /** A Phosphor icon element, e.g. `<PackageIcon size={20} />`. Omit for a text-only notice. */
  icon?: ReactNode;
  title: ReactNode;
  /** Sits under the title, muted. */
  description?: ReactNode;
  /** Sits under the description — typically a Button. */
  action?: ReactNode;
  className?: string;
};

/**
 * The page or table's "nothing here" block: an optional icon, a title, an
 * optional description and an optional action, centered and spaced for a
 * blank list or a `Table`'s `renderEmptyState`. The order-details page's
 * inline one-line notices (no icon, no title) are a separate, denser pattern
 * and are not replaced by this component.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'sk:flex sk:flex-col sk:items-center sk:gap-3 sk:px-6 sk:py-10 sk:text-center',
        className,
      )}
    >
      {icon ? (
        <div className="sk:flex sk:size-10 sk:items-center sk:justify-center sk:rounded-full sk:bg-fill-tertiary sk:text-fg-secondary">
          {icon}
        </div>
      ) : null}
      <p className="sk:m-0 sk:text-sm sk:font-semibold sk:text-fg">{title}</p>
      {description ? (
        <p className="sk:mx-auto sk:m-0 sk:max-w-80 sk:text-dense sk:text-fg-tertiary">
          {description}
        </p>
      ) : null}
      {action}
    </div>
  );
}
